import { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import SignatureCanvas from 'react-signature-canvas';

import { useSaveMySignMutation } from '@features/employee/hooks';
import Alert from '@shared/components/atoms/Alert';
import MobileModalHeader from '@shared/components/atoms/MobileModalHeader';
import { useAlert } from '@shared/hooks';
import refreshIcon from '@shared/assets/images/refresh_icon.svg';

import styles from './MobileSignEditScreen.module.scss';

/**
 * 모바일 서명 수정 화면 컴포넌트
 * - PC 환경의 SignSettingModal 기능을 모바일 사이즈로 조절
 * - 화면 전환 방식으로 동작 (모달 아님)
 */
function MobileSignEditScreen({ isOpen, onClose, onSuccess, signImageUrl }) {
  const signRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isImageMode, setIsImageMode] = useState(false);

  // Alert 상태 관리
  const { alertState, showAlert, closeAlert } = useAlert();

  // 서명 저장 Mutation
  const saveMutation = useSaveMySignMutation({
    onSuccess: function () {
      showAlert('서명이 등록되었습니다.', function () {
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      });
    },
    onError: function () {
      showAlert('서명 등록에 실패했습니다.');
    },
  });

  // 화면이 열릴 때 초기화 (기존 서명이 있으면 표시)
  useEffect(
    function () {
      if (isOpen) {
        if (signImageUrl) {
          setUploadedImage(signImageUrl);
          setIsImageMode(true);
        } else {
          setUploadedImage(null);
          setIsImageMode(false);
          signRef.current?.clear();
        }
      }
    },
    [isOpen, signImageUrl],
  );

  // 화면이 열릴 때 body 스크롤 방지
  useEffect(
    function () {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      return function () {
        document.body.style.overflow = '';
      };
    },
    [isOpen],
  );

  /**
   * 리프레시 버튼 클릭 핸들러 (지우기 기능)
   * - 서명 캔버스 및 업로드된 이미지 초기화
   */
  const handleRefresh = useCallback(function () {
    if (signRef.current) {
      signRef.current.clear();
    }
    setUploadedImage(null);
    setIsImageMode(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  /**
   * 파일 선택 핸들러
   */
  const handleFileSelect = useCallback(
    function (e) {
      const file = e.target.files[0];
      if (!file) return;

      // 파일 형식 검증 (jpg, png만 허용)
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        showAlert('jpg, png 형식의 파일만 업로드 가능합니다.');
        e.target.value = '';
        return;
      }

      // 파일 크기 검증 (5MB 제한)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        showAlert('파일 크기는 5MB 이하로 제한됩니다.');
        e.target.value = '';
        return;
      }

      // 이미지 미리보기 생성
      const reader = new FileReader();
      reader.onload = function (event) {
        setUploadedImage(event.target.result);
        setIsImageMode(true);
        // 서명 캔버스 초기화
        if (signRef.current) {
          signRef.current.clear();
        }
      };
      reader.readAsDataURL(file);
    },
    [showAlert],
  );

  /**
   * 서명파일 선택 버튼 클릭 핸들러
   */
  const handleSelectFileClick = useCallback(function () {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  /**
   * 서명 등록 핸들러
   */
  const handleSave = useCallback(
    function () {
      // 이미지 업로드 모드인 경우
      if (isImageMode && uploadedImage) {
        // Base64 → Blob 변환
        fetch(uploadedImage)
          .then(function (res) {
            return res.blob();
          })
          .then(function (blob) {
            saveMutation.mutate(blob);
          })
          .catch(function () {
            showAlert('이미지 처리 중 오류가 발생했습니다.');
          });
        return;
      }

      // 서명 드로잉 모드인 경우
      if (!signRef.current || signRef.current.isEmpty()) {
        showAlert('서명을 먼저 작성해주세요.');
        return;
      }

      const canvas = signRef.current.getCanvas();
      canvas.toBlob(function (blob) {
        if (!blob) {
          showAlert('이미지 생성에 실패했습니다.');
          return;
        }
        saveMutation.mutate(blob);
      }, 'image/png');
    },
    [isImageMode, uploadedImage, saveMutation, showAlert],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className={styles.screen}>
        {/* 헤더 */}
        <MobileModalHeader title="서명 설정" onBack={onClose} />

        {/* 본문 */}
        <div className={styles.content}>
          {/* 섹션 헤더 */}
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>서명 변경</span>
            <div className={styles.headerButtons}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className={styles.hiddenInput}
              />
              {/* <button
                type="button"
                className={styles.fileRegisterButton}
                onClick={handleSelectFileClick}
              >
                <span className={styles.fileRegisterText}>서명 파일 등록</span>
              </button> */}
            </div>
          </div>

          {/* 서명 드로잉 영역 */}
          <div className={styles.signCanvasWrapper}>
            {isImageMode && uploadedImage ? (
              <div className={styles.uploadedImageContainer}>
                <img src={uploadedImage} alt="업로드된 서명" className={styles.uploadedImage} />
              </div>
            ) : (
              <SignatureCanvas
                ref={signRef}
                penColor="black"
                canvasProps={{
                  className: styles.signCanvas,
                }}
              />
            )}
            <button
              type="button"
              className={styles.refreshButton}
              onClick={handleRefresh}
              aria-label="지우기"
            >
              <img src={refreshIcon} alt="지우기" className={styles.refreshIcon} />
            </button>
          </div>
        </div>

        {/* 하단 등록 버튼 */}
        <div className={styles.buttonGroup}>
          <button
            type="button"
            className={styles.submitButton}
            onClick={handleSave}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>

      {/* Alert 컴포넌트 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </>
  );
}

MobileSignEditScreen.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  signImageUrl: PropTypes.string,
};

MobileSignEditScreen.defaultProps = {
  onSuccess: null,
  signImageUrl: null,
};

export default MobileSignEditScreen;
